import React, { useEffect, useState } from 'react';
import type {
  AdminStats,
  BarChartProps,
  KpiCardProps,
  DonutSlice,
} from '../../../../types/admin';
import './AdminOverview.css';
import { adminService } from '../../../../services';

// ── Helpers ──────────────────────────────────────────────────

const formatEur = (n: number) =>
  new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(n);

const formatNum = (n: number) => new Intl.NumberFormat('it-IT').format(n);

const MONTHS_SHORT = [
  'Gen',
  'Feb',
  'Mar',
  'Apr',
  'Mag',
  'Giu',
  'Lug',
  'Ago',
  'Set',
  'Ott',
  'Nov',
  'Dic',
];

const getLast6Months = (): string[] => {
  const now = new Date();
  return Array.from({ length: 6 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - 5 + i, 1);
    return MONTHS_SHORT[d.getMonth()];
  });
};

const getLast7Days = (): string[] => {
  const days = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - 6 + i);
    return days[d.getDay()];
  });
};

// ── Tipi interni ─────────────────────────────────────────────

type ActiveTab = 'overview' | 'stats';

// ── Skeleton ─────────────────────────────────────────────────

const KpiSkeleton: React.FC = () => (
  <div className="ao-kpi-grid">
    {Array.from({ length: 6 }).map((_, i) => (
      <div key={i} className="ao-kpi-card ao-skeleton-card">
        <div className="ao-skeleton ao-skeleton--label" />
        <div className="ao-skeleton ao-skeleton--value" />
        <div className="ao-skeleton ao-skeleton--sub" />
      </div>
    ))}
  </div>
);

// ── BarChart CSS puro ─────────────────────────────────────────
const BarChart: React.FC<BarChartProps> = ({
  values,
  labels,
  color = 'var(--primary)',
  formatValue = String,
}) => {
  const max = Math.max(...values, 1);
  return (
    <div className="ao-bar-chart" role="img" aria-label="Grafico a barre">
      <div className="ao-bar-chart__bars">
        {values.map((v, i) => (
          <div key={i} className="ao-bar-wrap">
            <span className="ao-bar-value">{formatValue(v)}</span>
            <div
              className="ao-bar"
              style={{
                height: `${Math.max((v / max) * 100, 4)}%`,
                backgroundColor: color,
              }}
              title={`${labels[i]}: ${formatValue(v)}`}
            />
            <span className="ao-bar-label">{labels[i]}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── DonutChart CSS puro ───────────────────────────────────────

const DonutChart: React.FC<{ slices: DonutSlice[] }> = ({ slices }) => {
  const total = slices.reduce((s, sl) => s + sl.value, 0);
  let cumulative = 0;

  const radius = 40;
  const cx = 60;
  const cy = 60;
  const circumference = 2 * Math.PI * radius;

  return (
    <div className="ao-donut-wrap">
      <svg viewBox="0 0 120 120" className="ao-donut-svg" aria-hidden="true">
        {slices.map((sl, i) => {
          const pct = sl.value / total;
          const offset = circumference * (1 - pct);
          const rotation = cumulative * 360 - 90;
          cumulative += pct;
          return (
            <circle
              key={i}
              cx={cx}
              cy={cy}
              r={radius}
              fill="none"
              stroke={sl.color}
              strokeWidth="18"
              strokeDasharray={`${circumference * pct} ${circumference * (1 - pct)}`}
              strokeDashoffset={0}
              transform={`rotate(${rotation} ${cx} ${cy})`}
              style={{ transition: 'stroke-dasharray 0.6s ease' }}
            />
          );
        })}
        <circle cx={cx} cy={cy} r="29" fill="var(--bg-body-alt)" />
        <text
          x={cx}
          y={cy - 4}
          textAnchor="middle"
          fontSize="11"
          fontWeight="700"
          fill="var(--text-body)"
        >
          {formatNum(total)}
        </text>
        <text
          x={cx}
          y={cy + 10}
          textAnchor="middle"
          fontSize="7"
          fill="var(--text-body-secondary)"
        >
          iscrizioni
        </text>
      </svg>

      <ul className="ao-donut-legend">
        {slices.map((sl, i) => (
          <li key={i} className="ao-donut-legend-item">
            <span
              className="ao-donut-legend-dot"
              style={{ backgroundColor: sl.color }}
            />
            <span className="ao-donut-legend-label">{sl.label}</span>
            <span className="ao-donut-legend-value">
              {sl.value} ({Math.round((sl.value / total) * 100)}%)
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

// ── KPI Card ─────────────────────────────────────────────────

const KpiCard: React.FC<KpiCardProps> = ({
  icon,
  label,
  value,
  sub,
  highlight,
  trend,
}) => (
  <div className={`ao-kpi-card ${highlight ? 'ao-kpi-card--highlight' : ''}`}>
    <div className="ao-kpi-header">
      <span className="ao-kpi-icon">
        <i className={`bi ${icon}`} aria-hidden="true" />
      </span>
      {trend && (
        <span className={`ao-kpi-trend ao-kpi-trend--${trend}`}>
          <i
            className={`bi ${
              trend === 'up'
                ? 'bi-arrow-up-short'
                : trend === 'down'
                  ? 'bi-arrow-down-short'
                  : 'bi-dash'
            }`}
          />
        </span>
      )}
    </div>
    <p className="ao-kpi-label">{label}</p>
    <p className="ao-kpi-value">{value}</p>
    {sub && <p className="ao-kpi-sub">{sub}</p>}
  </div>
);

// ── Main component ────────────────────────────────────────────

const AdminOverview: React.FC = () => {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState<ActiveTab>('overview');

  useEffect(() => {
    adminService
      .getStats()
      .then(setStats)
      .catch(() => setError('Impossibile caricare le statistiche.'))
      .finally(() => setLoading(false));
  }, []);

  const last6Months = getLast6Months();
  const last7Days = getLast7Days();

  const donutSlices = stats
    ? [
        {
          label: 'Be Smart',
          value: stats.courseEnrollments[0]?.count ?? 0,
          color: 'var(--primary)',
        },
        {
          label: 'Postura Pro',
          value: stats.courseEnrollments[1]?.count ?? 0,
          color: 'var(--secondary)',
        },
        {
          label: 'Core Reset',
          value: stats.courseEnrollments[2]?.count ?? 0,
          color: 'var(--tertiary)',
        },
      ]
    : [];

  if (error) {
    return (
      <div className="ao-page">
        <div className="ao-error" role="alert">
          <i className="bi bi-exclamation-triangle me-2" />
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="ao-page">
      {/* ── Page header ── */}
      <div className="ao-page-header">
        <div>
          <h1 className="ao-page-title">Panoramica</h1>
          <p className="ao-page-sub">
            Aggiornato al{' '}
            {new Date().toLocaleDateString('it-IT', {
              day: '2-digit',
              month: 'long',
              year: 'numeric',
            })}
          </p>
        </div>
      </div>

      {/* ── Tab selector ── */}
      <div className="ao-tabs" role="tablist">
        {(['overview', 'stats'] as ActiveTab[]).map((t) => (
          <button
            key={t}
            role="tab"
            aria-selected={tab === t}
            className={`ao-tab ${tab === t ? 'ao-tab--active' : ''}`}
            onClick={() => setTab(t)}
          >
            <i
              className={`bi ${t === 'overview' ? 'bi-grid-1x2' : 'bi-bar-chart-line'} me-2`}
            />
            {t === 'overview' ? 'Panoramica' : 'Statistiche'}
          </button>
        ))}
      </div>

      {/* ══════════════════════════════════════
          TAB: OVERVIEW
      ══════════════════════════════════════ */}
      {tab === 'overview' && (
        <>
          {loading ? (
            <KpiSkeleton />
          ) : (
            stats && (
              <>
                {/* KPI grid */}
                <div className="ao-kpi-grid">
                  <KpiCard
                    icon="bi-people"
                    label="Utenti totali"
                    value={formatNum(stats.totalUsers)}
                    sub={`${stats.activeUsers} attivi`}
                    trend="up"
                  />
                  <KpiCard
                    icon="bi-person-check"
                    label="Utenti attivi"
                    value={formatNum(stats.activeUsers)}
                    sub={`${Math.round((stats.activeUsers / stats.totalUsers) * 100)}% del totale`}
                    trend="neutral"
                  />
                  <KpiCard
                    icon="bi-cash-stack"
                    label="Ricavi totali"
                    value={formatEur(stats.totalRevenue)}
                    sub={`${formatEur(stats.revenueThisMonth)} questo mese`}
                    highlight
                    trend="up"
                  />
                  <KpiCard
                    icon="bi-graph-up-arrow"
                    label="Ricavi del mese"
                    value={formatEur(stats.revenueThisMonth)}
                    sub={`su ${stats.ordersThisMonth} ordini`}
                    trend="up"
                  />
                  <KpiCard
                    icon="bi-receipt"
                    label="Ordini totali"
                    value={formatNum(stats.totalOrders)}
                    sub={`${stats.ordersThisMonth} questo mese`}
                    trend="neutral"
                  />
                  <KpiCard
                    icon="bi-collection-play"
                    label="Corsi disponibili"
                    value={formatNum(stats.activeCourses)}
                    sub="sulla piattaforma"
                    trend="neutral"
                  />
                </div>

                {/* Nuovi iscritti + iscrizioni ai corsi */}
                <div className="ao-charts-row">
                  <div className="ao-chart-card">
                    <h2 className="ao-chart-title">
                      <i className="bi bi-person-plus me-2" />
                      Nuovi iscritti — ultimi 7 giorni
                    </h2>
                    <BarChart
                      values={stats.weeklySignups}
                      labels={last7Days}
                      color="var(--primary)"
                    />
                  </div>

                  <div className="ao-chart-card">
                    <h2 className="ao-chart-title">
                      <i className="bi bi-pie-chart me-2" />
                      Iscrizioni per corso
                    </h2>
                    <DonutChart slices={donutSlices} />
                  </div>
                </div>
              </>
            )
          )}
        </>
      )}

      {/* ══════════════════════════════════════
          TAB: STATISTICHE
      ══════════════════════════════════════ */}
      {tab === 'stats' && (
        <>
          {loading ? (
            <div className="ao-kpi-grid">
              {Array.from({ length: 2 }).map((_, i) => (
                <div
                  key={i}
                  className="ao-chart-card ao-skeleton-card"
                  style={{ minHeight: 220 }}
                >
                  <div
                    className="ao-skeleton ao-skeleton--label"
                    style={{ width: '40%' }}
                  />
                  <div
                    className="ao-skeleton"
                    style={{
                      height: 140,
                      marginTop: '1rem',
                      borderRadius: '0.5rem',
                    }}
                  />
                </div>
              ))}
            </div>
          ) : (
            stats && (
              <>
                {/* Vendite mensili */}
                <div className="ao-chart-card ao-chart-card--full">
                  <h2 className="ao-chart-title">
                    <i className="bi bi-graph-up me-2" />
                    Vendite mensili — ultimi 6 mesi
                  </h2>
                  <BarChart
                    values={stats.monthlySales}
                    labels={last6Months}
                    color="var(--secondary)"
                    formatValue={(v) => `€${v}`}
                  />
                </div>

                {/* Tabella iscrizioni per corso */}
                <div className="ao-chart-card ao-chart-card--full">
                  <h2 className="ao-chart-title">
                    <i className="bi bi-collection-play me-2" />
                    Iscrizioni per corso
                  </h2>
                  <div className="ao-table-wrap">
                    <table className="ao-table">
                      <thead>
                        <tr>
                          <th>Corso</th>
                          <th className="ao-table-num">Iscritti</th>
                          <th>Distribuzione</th>
                          <th className="ao-table-num">% sul totale</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.courseEnrollments.map((c) => {
                          const total = stats.courseEnrollments.reduce(
                            (s, e) => s + e.count,
                            0
                          );
                          const pct = Math.round((c.count / total) * 100);
                          return (
                            <tr key={c.courseId}>
                              <td className="ao-table-name">{c.title}</td>
                              <td className="ao-table-num">
                                <strong>{formatNum(c.count)}</strong>
                              </td>
                              <td>
                                <div className="ao-progress-bar">
                                  <div
                                    className="ao-progress-fill"
                                    style={{ width: `${pct}%` }}
                                  />
                                </div>
                              </td>
                              <td className="ao-table-num ao-table-pct">
                                {pct}%
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Riepilogo numerico */}
                <div className="ao-stats-summary">
                  {[
                    {
                      label: 'Tasso conversione (stimato)',
                      value: `${Math.round((stats.totalOrders / stats.totalUsers) * 100)}%`,
                      formula: 'ordini totali ÷ utenti totali',
                    },
                    {
                      label: 'Valore medio ordine',
                      value: formatEur(
                        Math.round(stats.totalRevenue / stats.totalOrders)
                      ),
                      formula: 'ricavi totali ÷ ordini totali',
                    },
                    {
                      label: 'Ricavo medio per utente',
                      value: formatEur(
                        Math.round(stats.totalRevenue / stats.activeUsers)
                      ),
                      formula: 'ricavi totali ÷ utenti attivi',
                    },
                    {
                      label: 'Ordini per utente attivo',
                      value: (stats.totalOrders / stats.activeUsers).toFixed(1),
                      formula: 'ordini totali ÷ utenti attivi',
                    },
                  ].map((item, i) => (
                    <div key={i} className="ao-stats-summary-item">
                      <p className="ao-stats-summary-label">{item.label}</p>
                      <p className="ao-stats-summary-value">{item.value}</p>
                      <p className="ao-stats-summary-formula">{item.formula}</p>
                    </div>
                  ))}
                </div>

                {/* Legenda: cosa significano esattamente questi numeri */}
                <div className="ao-legend">
                  <h3 className="ao-legend-title">
                    <i className="bi bi-info-circle me-2" />
                    Come sono calcolati
                  </h3>
                  <dl className="ao-legend-list">
                    <dt>Tasso conversione (stimato)</dt>
                    <dd>
                      Numero di ordini completati diviso il numero di utenti
                      registrati, in percentuale. È una stima grezza, non un
                      vero tasso di conversione (che richiederebbe il numero di
                      visitatori del sito, non tracciato): se pochi utenti
                      acquistano più volte, o comprano più corsi, il valore può
                      superare il 100%.
                    </dd>
                    <dt>Valore medio ordine</dt>
                    <dd>
                      Ricavi totali (somma degli importi degli ordini
                      completati) diviso il numero di ordini completati.
                    </dd>
                    <dt>Ricavo medio per utente</dt>
                    <dd>
                      Ricavi totali diviso gli utenti "attivi" (tutti gli
                      account non bloccati, inclusi quelli gratuiti che non
                      hanno mai acquistato nulla) — non solo chi ha
                      effettivamente pagato.
                    </dd>
                    <dt>Ordini per utente attivo</dt>
                    <dd>
                      Numero di ordini completati diviso gli utenti "attivi",
                      stessa definizione del punto precedente.
                    </dd>
                    <dt>Iscritti (grafico a torta / tabella corsi)</dt>
                    <dd>
                      Utenti che hanno accesso al corso in questo momento (dato
                      preso dagli acquisti registrati), non il numero di ordini
                      per quel corso: un utente che ha comprato più volte lo
                      stesso corso per errore conta come 1 iscritto, non più
                      volte.
                    </dd>
                  </dl>
                </div>
              </>
            )
          )}
        </>
      )}
    </div>
  );
};

export default AdminOverview;
